import { useState, useEffect, useMemo } from "react";
import { PageHeader, EmptyState, Skeleton } from "@/components/Primitives";
import { fmtDate } from "@/lib/format";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FileText, Download, Search, Inbox, FolderOpen } from "lucide-react";

export default function MyResources() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/library/mine").then((r) => setDocs(r.data.items)).finally(() => setLoading(false));
  }, []);

  const download = async (d) => {
    try {
      const { data } = await api.get(`/library/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename || d.title; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => docs.filter((d) =>
    !q || d.title.toLowerCase().includes(q.toLowerCase()) || (d.category || "").toLowerCase().includes(q.toLowerCase())), [docs, q]);
  const shared = filtered.filter((d) => d.shared_with_me);
  const general = filtered.filter((d) => !d.shared_with_me);

  const Card = ({ d, badge }) => (
    <div className="cc-card p-4 flex flex-col" data-testid={`myres-${d.id}`}>
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 rounded-lg bg-[#00C9E0]/10 text-[#0094A8] flex items-center justify-center shrink-0"><FileText className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-semibold text-slate-800 text-sm truncate">{d.title}</h3>
          <p className="text-xs text-slate-400 truncate">{d.category} · v{d.version} · {fmtDate(d.created_at)}</p>
        </div>
        {badge && <span className="rounded-full bg-[#9ACC32]/15 text-[#5B880C] px-2 py-0.5 text-[10px] font-semibold shrink-0">Shared</span>}
      </div>
      {d.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{d.description}</p>}
      <Button size="sm" variant="outline" className="h-7 text-xs mt-3" onClick={() => download(d)} data-testid={`myres-download-${d.id}`}><Download className="h-3 w-3" />Open / Download</Button>
    </div>
  );

  return (
    <div data-testid="page-my-resources">
      <PageHeader title="My Resources" subtitle="Documents shared with you and resources available to your role." />
      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input className="pl-9" placeholder="Search resources…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="myres-search" />
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-36" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="cc-card"><EmptyState icon={Inbox} title="No resources yet" hint="Documents shared with you and resources for your role will appear here." /></div>
      ) : (
        <div className="space-y-8">
          {shared.length > 0 && (
            <section>
              <h2 className="font-display font-semibold text-sm text-slate-800 mb-3 flex items-center gap-2"><Inbox className="h-4 w-4 text-[#00C9E0]" />Shared With Me ({shared.length})</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{shared.map((d) => <Card key={d.id} d={d} badge />)}</div>
            </section>
          )}
          <section>
            <h2 className="font-display font-semibold text-sm text-slate-800 mb-3 flex items-center gap-2"><FolderOpen className="h-4 w-4 text-[#9ACC32]" />Available Resources ({general.length})</h2>
            {general.length === 0 ? <p className="text-sm text-slate-400">No general resources available.</p> : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{general.map((d) => <Card key={d.id} d={d} />)}</div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
